<template>
  <v-container fluid>
    <v-row wrap>
      <v-col xs="12" class="headline">
        <v-btn
          color="primary"
          class="mr-2"
          small
          outlined
          text
          to="/jobs"
          style="margin-top:-3px"
          ><v-icon>chevron_left</v-icon></v-btn
        >
        <span v-if="job" class="ml-4">{{ job.street }}</span>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="12" lg="6">
        <v-card v-if="job">
          <v-card-text>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-container>
                <v-row>
                  <v-col cols="12" xs="12" sm="6">
                    <v-select
                      v-model="job.status"
                      :items="['open', 'closed', 'draft']"
                      label="Job Status"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mb-4">
                  <v-col cols="12" xs="12" sm="6">
                    <v-autocomplete
                      v-model="job.estimator"
                      :items="pmList"
                      attach
                      label="Estimator"
                      return-object
                      item-text="name"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dateAwardedRef"
                      v-model="job.dialogDateAwarded"
                      :return-value.sync="job.dateAwarded"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsAwarded"
                          label="Date Awarded"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="job.dateAwarded" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="dialogDateAwarded = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dateAwardedRef.save(job.dateAwarded)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="job.street"
                      label="Street Address"
                      required
                      :rules="[(v) => !!v || 'Street is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="job.street2"
                      label="Street Address Line 2"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="5">
                    <v-text-field
                      v-model.trim="job.city"
                      label="City"
                      required
                      :rules="[(v) => !!v || 'City is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="3">
                    <v-select
                      v-model="job.state"
                      :items="statesList"
                      label="State"
                      required
                      :rules="[(v) => !!v || 'State is required']"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12" sm="4">
                    <v-text-field
                      v-model.trim="job.postalCode"
                      label="Zip Code"
                      required
                      :rules="[(v) => !!v || 'Zip Code is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-alert type="info" outlined dense class="mb-0">
                      <div class="d-flex align-center justify-space-between">
                        <span
                          >Find new coordinates using the address above</span
                        >
                        <v-btn
                          color="primary"
                          small
                          :loading="findingCoordinates"
                          :disabled="
                            !job.street ||
                              !job.city ||
                              !job.state ||
                              !job.postalCode ||
                              !!foundCoordinates
                          "
                          @click="findCoordinates"
                        >
                          <v-icon small left>location_searching</v-icon>
                          Find Coordinates
                        </v-btn>
                      </div>
                    </v-alert>
                    <v-alert
                      v-if="coordinateSearchAttempted && !foundCoordinates"
                      type="error"
                      dense
                      class="mt-2"
                    >
                      <div>
                        <strong
                          >Unable to find coordinates for this address</strong
                        >
                        <div class="caption mt-1">
                          Searched for: {{ searchedAddress }}
                        </div>
                        <div class="caption mt-1">
                          Please verify the address is correct and try again. If
                          you save, existing coordinates will be removed.
                        </div>
                      </div>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" v-if="foundCoordinates">
                    <v-alert color="success" outlined dense class="mb-3">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="font-weight-medium mb-1">
                            New Coordinates Found
                          </div>
                          <div class="caption">
                            Searched: {{ searchedAddress }}
                          </div>
                        </div>
                        <v-btn
                          icon
                          small
                          @click="clearCoordinates"
                          color="error"
                        >
                          <v-icon small>close</v-icon>
                        </v-btn>
                      </div>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6" v-if="foundCoordinates">
                    <v-text-field
                      :value="foundCoordinates.latitude"
                      label="New Latitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                      success
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6" v-if="foundCoordinates">
                    <v-text-field
                      :value="foundCoordinates.longitude"
                      label="New Longitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                      success
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    xs="12"
                    sm="6"
                    v-if="job.geolocation && !foundCoordinates"
                  >
                    <v-text-field
                      :value="job.geolocation.latitude"
                      label="Current Latitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    xs="12"
                    sm="6"
                    v-if="job.geolocation && !foundCoordinates"
                  >
                    <v-text-field
                      :value="job.geolocation.longitude"
                      label="Current Longitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" v-if="!job.geolocation && !foundCoordinates">
                    <v-alert type="warning" dense outlined>
                      <v-icon small left>warning</v-icon>
                      This job does not have coordinates set. Use "Find
                      Coordinates" above to set them.
                    </v-alert>
                  </v-col>
                  <v-col cols="12" xs="12">
                    <v-text-field
                      v-model.trim="job.jobId"
                      label="Job Id (example: 20-2347)"
                      required
                      :rules="[(v) => !!v || 'Job Number is required']"
                    ></v-text-field>
                    <div class="red--text" v-if="dupeJobIdCheck(job)">
                      * WARNING: Job Id already exists (you can still create the
                      job)
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      v-model.trim="job.proposalNumber"
                      label="Proposal Number"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-select
                      v-model="job.entity"
                      :items="['Volk', 'Alba Services']"
                      label="Entity"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12">
                    <v-select
                      v-model="job.department"
                      chips
                      :items="[
                        'Demolition',
                        'Core & Shell',
                        'Concrete & Steel',
                        'Asbestos',
                        'Electrical',
                        'T&M',
                        'External Truck Request',
                        'Fireproofing',
                        'Superstructure',
                      ]"
                      label="Choose department(s)"
                      :rules="[(v) => !!v.length || 'Department is required']"
                      multiple
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mb-4">
                  <v-col cols="12" class="mt-4">
                    <h4>Project Managers</h4>
                    <span class="caption text--grey"
                      >Select project managers for this job and setup email
                      notifications</span
                    >
                    <v-autocomplete
                      @change="updatePmList"
                      v-model="job.projectManagers"
                      :items="pmList"
                      attach
                      chips
                      label="Select PM's"
                      multiple
                      return-object
                      item-text="name"
                      deletable-chips
                    >
                    </v-autocomplete>
                    <p v-if="job.projectManagers.length" class="mb-2">
                      <v-icon small>mail_outline</v-icon> Email Notifications
                      Settings
                    </p>
                    <v-card
                      v-if="job.projectManagers.length"
                      color="#f1f1f1"
                      elevation="1"
                    >
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left"></th>
                              <th class="text-left">Tool Orders</th>
                              <th class="text-left">Tool Pickups</th>
                              <th class="text-left">
                                Consumables Budget Email
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="pm in job.projectManagers"
                              :key="pm.email"
                            >
                              <td>{{ pm.name }}</td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.toolOrders"
                                  color="green"
                                ></v-switch>
                              </td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.toolPickups"
                                  color="green"
                                ></v-switch>
                              </td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.consumablesBudget"
                                  color="green"
                                ></v-switch>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-4 pt-4">
                  <v-col cols="12">
                    <h4>Scope & Budgets</h4>
                    <div class="caption text--grey mb-4">
                      Enter budget amounts. Entering a consumables budget amount
                      will send an email alert once that amount is reached.
                      Example: If the budget is 10K you may want to be alerted
                      when it reaches 8K.
                    </div>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Contract Value"
                      v-model.number="job.contractValue"
                      prepend-icon="monetization_on"
                      :hint="job.contractValue | currency"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Scope Breakdown Notes"
                      v-model="job.scopeBreakdownNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Total Estimated Budget"
                      v-model.number="job.totalBudget"
                      :hint="job.totalBudget | currency"
                      persistent-hint
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget Notes"
                      v-model="job.totalBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - LABOR"
                      v-model.number="job.laborBudget"
                      :hint="job.laborBudget | currency"
                      persistent-hint
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Man Days"
                      v-model.number="job.manDays"
                      type="number"
                      prepend-icon="person"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="12">
                    <v-text-field
                      label="Labor Budget Notes"
                      v-model="job.laborBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - DUMPING"
                      v-model.number="job.dumpingBudget"
                      :hint="job.dumpingBudget | currency"
                      persistent-hint
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Dumping Budget Notes"
                      v-model="job.dumpingBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - MATERIAL"
                      v-model.number="job.materialBudget"
                      :hint="job.materialBudget | currency"
                      persistent-hint
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Material Budget Notes"
                      v-model="job.materialBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - CONSUMABLES"
                      v-model.number="job.consumablesBudget"
                      :hint="job.consumablesBudget | currency"
                      persistent-hint
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Consumables Budget - Alert Amount"
                      v-model.number="job.consumablesBudgetAlert"
                      :hint="job.consumablesBudgetAlert | currency"
                      persistent-hint
                      prepend-icon="notifications"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mb-4">
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Contract Value"
                      v-model.number="job.subcontractorValue"
                      prepend-icon="monetization_on"
                      :hint="job.subcontractorValue | currency"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dialogStart"
                      v-model="job.dialogStart"
                      :return-value.sync="job.estimatedStartDate"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsStart"
                          label="Estimated Start Date"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          prepend-icon="today"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="job.estimatedStartDate"
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dialogStart = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.dialogStart.save(job.estimatedStartDate)
                          "
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dialogEnd"
                      v-model="job.dialogEnd"
                      :return-value.sync="job.estimatedCompletionDate"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsEnd"
                          label="Estimated Completion Date"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          prepend-icon="event_available"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="job.estimatedCompletionDate"
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dialogEnd = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.dialogEnd.save(job.estimatedCompletionDate)
                          "
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="">
                    <p class="text--grey">
                      If turned on, anyone punching in on this jobsite will be
                      excluded from the payroll lunch rule.
                    </p>
                    <v-switch
                      v-model="job.ignoreLunchRule"
                      color="green"
                      label="Ignore Payroll Lunch Rule"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" xs="12">
                    <v-textarea
                      outlined
                      v-model="job.operationsNotes"
                      label="Operations Notes"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <h4>Daily Report External Emails</h4>
                    <div class="caption text--grey mb-4">
                      Add external email addresses that should receive daily
                      reports for this job site.
                    </div>
                    <v-text-field
                      v-model="newEmail"
                      label="Add Email Address"
                      type="email"
                      @keyup.enter="addEmail"
                      @input="clearEmailError"
                      :append-icon="newEmail ? 'add' : null"
                      @click:append="addEmail"
                      :clearable="!!newEmail"
                      :error-messages="emailError"
                      :error="!!emailError"
                      hide-details="auto"
                    ></v-text-field>
                    <div
                      v-if="
                        job.dailyReportExternalEmails &&
                          job.dailyReportExternalEmails.length
                      "
                      class="mb-2"
                    >
                      <v-chip
                        v-for="email in job.dailyReportExternalEmails"
                        :key="email"
                        close
                        @click:close="removeEmail(email)"
                        color="primary"
                        outlined
                        class="ma-1"
                      >
                        {{ email }}
                      </v-chip>
                    </div>
                    <div v-else class="text--grey caption">
                      No external emails added yet.
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="px-0">
                    <div class="text-right mt-4 pt-4">
                      <v-btn
                        :disabled="!valid || submitting"
                        color="primary"
                        class="mr-4"
                        large
                        :loading="submitting"
                        @click="saveJob"
                        ><v-icon small left>save</v-icon> Save</v-btn
                      >
                    </div>
                    <br />
                    <br />
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import utils from "@/helpers/utils";
import fb from "@/main";
import { mapState } from "vuex";
import moment from "moment";
import firebase from "firebase/app";

export default {
  data() {
    return {
      job: null,
      statesList: utils.statesListShort,
      valid: true,
      pmList: [],
      dialogDateAwarded: false,
      dialogStart: false,
      dialogEnd: false,
      submitting: false,
      snack: false,
      snackColor: "",
      snackText: "",
      newEmail: "",
      emailError: "",
      // coordinates finding
      findingCoordinates: false,
      foundCoordinates: null,
      coordinateSearchAttempted: false,
      searchedAddress: "",
    };
  },
  async created() {
    await this.getJob();
    // Estimator + PM selectors: active Project Manager & Office users, sorted A–Z
    this.pmList = await utils.getJobAssignees();
  },
  computed: {
    ...mapState(["user", "jobSites"]),
    computedDateFormattedMomentjsAwarded() {
      return this.job.dateAwarded
        ? moment(this.job.dateAwarded).format("M/DD/YYYY")
        : "";
    },
    computedDateFormattedMomentjsStart() {
      return this.job.estimatedStartDate
        ? moment(this.job.estimatedStartDate).format("M/DD/YYYY")
        : "";
    },
    computedDateFormattedMomentjsEnd() {
      return this.job.estimatedCompletionDate
        ? moment(this.job.estimatedCompletionDate).format("M/DD/YYYY")
        : "";
    },
  },
  methods: {
    async getJob() {
      try {
        let job = await fb.db
          .collection("jobSites")
          .doc(this.$route.params.id)
          .get();
        job = job.data();
        this.job = job;
      } catch (error) {
        alert("Error getting job.");
        console.log(error);
      }
    },

    // Geocode address using OpenStreetMap Nominatim
    async geocodeAddress(address) {
      try {
        // Construct full address string
        const addressString = `${address.street || ""} ${address.street2 ||
          ""} ${address.city || ""} ${address.state ||
          ""} ${address.postalCode || ""}`.trim();

        if (!addressString) {
          console.log("No address provided, skipping geocoding");
          return null;
        }

        console.log("Geocoding address:", addressString);

        // Using OpenStreetMap Nominatim (free, no API key required)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
            new URLSearchParams({
              q: addressString,
              format: "json",
              limit: 1,
            }),
          {
            headers: {
              "User-Agent": "Volk Industrial Services Jobsite App",
            },
          }
        );

        const data = await response.json();

        if (data && data.length > 0) {
          const result = data[0];
          console.log("Geocoding successful:", result.lat, result.lon);
          return {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
          };
        } else {
          console.log("No geocoding results found");
          return null;
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        return null;
      }
    },

    async saveJob() {
      try {
        this.submitting = true;
        this.job.address = `${this.job.street} ${this.job.street2 + " "}${
          this.job.city
        }, ${this.job.state} ${this.job.postalCode}`;
        const currencyFields = [
          "contractValue",
          "totalBudget",
          "laborBudget",
          "dumpingBudget",
          "materialBudget",
          "consumablesBudget",
          "consumablesBudgetAlert",
          "subcontractorValue",
        ];
        currencyFields.forEach((f) => {
          if (!this.job[f]) this.job[f] = 0;
        });

        // Ensure dailyReportExternalEmails is initialized as empty array if not set
        if (!this.job.dailyReportExternalEmails) {
          this.job.dailyReportExternalEmails = [];
        }

        // Update coordinates if new ones were found via "Find Coordinates" button
        // Skip for External Truck Request department
        const isExternalTruckRequest =
          this.job.department &&
          this.job.department.some((d) =>
            d.toLowerCase().includes("external truck request")
          );
        // Keep the flag getJobSites filters on in step with the department
        this.job.external = !!isExternalTruckRequest;

        if (!isExternalTruckRequest) {
          if (this.foundCoordinates) {
            // User found new coordinates - update them
            this.job.geolocation = new firebase.firestore.GeoPoint(
              this.foundCoordinates.latitude,
              this.foundCoordinates.longitude
            );
            console.log("Updated geolocation:", this.foundCoordinates);
          } else if (this.coordinateSearchAttempted && !this.foundCoordinates) {
            // User tried to find coordinates but failed - remove existing ones
            this.job.geolocation = firebase.firestore.FieldValue.delete();
            console.warn(
              "Coordinate search failed - removing geolocation coordinates"
            );
          }
          // If neither condition is true, keep existing coordinates unchanged
        }

        await fb.db
          .collection("jobSites")
          .doc(this.$route.params.id)
          .update(this.job);
        await this.getJob();

        // Clear the found coordinates after save
        this.foundCoordinates = null;
        this.coordinateSearchAttempted = false;
        this.searchedAddress = "";

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Successfully updated job!";
        this.submitting = false;
      } catch (error) {
        console.log(error);
        this.submitting = false;
        alert("Unable to save changes.");
      }
    },
    updatePmList() {
      this.pmList = this.pmList.map((pm) => {
        return {
          ...pm,
          notifications: {
            toolOrders: true,
            toolPickups: true,
            consumablesBudget: true,
          },
        };
      });
    },
    dupeJobIdCheck(job) {
      if (job.jobId && this.jobSites.length && job.jobId.length == 7) {
        return this.jobSites.some((j) => {
          return j.jobId === job.jobId && j.id !== this.$route.params.id;
        });
      }
    },
    addEmail() {
      this.emailError = "";

      if (!this.newEmail || !this.newEmail.trim()) {
        this.emailError = "Email is required";
        return;
      }

      if (!/.+@.+\..+/.test(this.newEmail.trim())) {
        this.emailError = "Email must be valid";
        return;
      }

      if (!this.job.dailyReportExternalEmails) {
        this.$set(this.job, "dailyReportExternalEmails", []);
      }

      if (this.job.dailyReportExternalEmails.includes(this.newEmail.trim())) {
        this.emailError = "Email already exists in the list";
        return;
      }

      // Use Vue.set to ensure reactivity when adding emails
      this.$set(this.job, "dailyReportExternalEmails", [
        ...this.job.dailyReportExternalEmails,
        this.newEmail.trim(),
      ]);
      this.newEmail = "";
      this.emailError = "";
    },
    removeEmail(email) {
      if (this.job.dailyReportExternalEmails) {
        const index = this.job.dailyReportExternalEmails.indexOf(email);
        if (index > -1) {
          // Use Vue.set to ensure reactivity
          this.$set(
            this.job,
            "dailyReportExternalEmails",
            this.job.dailyReportExternalEmails.filter((e) => e !== email)
          );
        }
      }
      // Clear any email error when removing emails
      this.emailError = "";
    },
    clearEmailError() {
      // Clear error when user starts typing or clears the field
      if (this.emailError) {
        this.emailError = "";
      }
      // If field is completely cleared, also clear error
      if (!this.newEmail) {
        this.emailError = "";
      }
    },
    async findCoordinates() {
      this.findingCoordinates = true;
      this.coordinateSearchAttempted = false; // Reset before starting
      this.foundCoordinates = null; // Clear any previous results

      try {
        const coords = await this.geocodeAddress({
          street: this.job.street,
          street2: this.job.street2,
          city: this.job.city,
          state: this.job.state,
          postalCode: this.job.postalCode,
        });

        const addressString = `${this.job.street || ""} ${this.job.street2 ||
          ""} ${this.job.city || ""} ${this.job.state || ""} ${this.job
          .postalCode || ""}`.trim();
        this.searchedAddress = addressString;

        if (coords) {
          this.foundCoordinates = coords;
          this.coordinateSearchAttempted = true;
        } else {
          this.foundCoordinates = null;
          this.coordinateSearchAttempted = true;
        }
      } catch (error) {
        console.error("Error finding coordinates:", error);
        this.foundCoordinates = null;
        this.searchedAddress = "Error occurred during geocoding";
        this.coordinateSearchAttempted = true;
      } finally {
        this.findingCoordinates = false;
      }
    },
    clearCoordinates() {
      this.foundCoordinates = null;
      this.coordinateSearchAttempted = false;
      this.searchedAddress = "";
    },
  },
};
</script>
<style scoped>
/* .col-12 {
  padding-bottom: 0 !important;
  padding-top: 0 !important;
} */
</style>
